import { isNil } from '@core/utils/shared-utils';

/**
 * A `view` from the messages endpoint, resolved into what it takes to display it.
 *
 * The response's `content_type` describes the channel, not the individual value: a
 * MULTIPLE_EMAILS configuration is `text/html` yet each view is `{ subject, html }`, and a
 * webhook is `application/json` yet its view may be either a raw string or parsed JSON. So
 * the value's own shape decides how it renders.
 */
export interface ResolvedDeployView {
  /** Only the per-message email shape carries a subject of its own. */
  subject: string | null;
  body: string;
  /** Whether `body` is a document to render, rather than text to show verbatim. */
  isHtml: boolean;
}

/** `{ subject, html }`, the shape a channel that renders one email per message sends. */
function resolveEmailView(view: object): ResolvedDeployView | null {
  const record = view as Record<string, unknown>;
  if (typeof record['html'] !== 'string') return null;
  return {
    subject: typeof record['subject'] === 'string' ? record['subject'] : null,
    body: record['html'],
    isHtml: true
  };
}

/**
 * Returns `null` when there is nothing to show, which is the only check a caller needs
 * before opening the viewer.
 */
export function resolveDeployView(
  view: unknown,
  contentType: string
): ResolvedDeployView | null {
  if (isNil(view)) return null;

  if (typeof view === 'string') {
    return { subject: null, body: view, isHtml: contentType.startsWith('text/html') };
  }

  if (typeof view === 'object') {
    const email = resolveEmailView(view);
    if (email !== null) return email;
  }

  // Anything else is shown as text whatever the content type says: a parsed webhook
  // payload handed to the HTML iframe renders as a blank page.
  return { subject: null, body: JSON.stringify(view, null, 2), isHtml: false };
}
